import {
  TPR_BILTMORE_AHI_DEPARTMENT_ID,
  TPR_BILTMORE_ARROWHEAD_DEPARTMENT_ID,
  TPR_BILTMORE_CASA_GRANDE_DEPARTMENT_ID,
  TPR_BILTMORE_CENTRAL_DEPARTMENT_ID,
  TPR_BILTMORE_DEPARTMENT_ID,
  TPR_BILTMORE_GOODYEAR_DEPARTMENT_ID,
  TPR_BILTMORE_PEORIA_DEPARTMENT_ID,
  TPR_BILTMORE_SCOTTSDALE_DEPARTMENT_ID,
  TPR_BILTMORE_SURPRISE_DEPARTMENT_ID,
  TPR_BILTMORE_VERRADO_DEPARTMENT_ID,
  TPR_BILTMORE_WEST_DEPARTMENT_ID,
} from "@/configuration/groups/tpr/athena";
import {
  getTprSupervisingPhysicianNpi,
  TprBiltmoreAhiProviderNpi,
  TprBiltmoreArrowheadProviderNpi,
  TprBiltmoreCamelbackProviderNpi,
  TprBiltmoreCasaGrandeProviderNpi,
  TprBiltmoreGoodyearProviderNpi,
  TprBiltmorePeoriaProviderNpi,
  TprBiltmoreScottsdaleProviderNpi,
  TprBiltmoreWestProviderNpi,
} from "@/configuration/groups/tpr/configuration";
import { textMatchesUnnegated } from "@/src/lib/utils/negated-keyword-match";
import type { DiagnosisItem, Referral } from "@/src/models/documents/referral";

/**
 * TPR's four cardiology sub-specialty categories, used by the receiving-provider resolver's
 * "no receiving provider on the referral, pick one who treats this diagnosis" branch (see
 * receiving-provider-location.ts). Distinct from the reviewer checklist's bucket-assignment logic
 * (sync-checklist.ts), which deliberately does NOT gate on specialty/diagnosis per ops request
 * (see the "drop specialty gate" commit) — that removal was scoped to bucket assignment only, not
 * to provider *selection* when the referral doesn't name one.
 */
export enum TprSpecialtyCategory {
  GENERAL = "general",
  INTERVENTIONAL = "interventional",
  STRUCTURAL = "structural",
  ELECTROPHYSIOLOGY = "electrophysiology",
}

type DiagnosisSpecialtyPattern = {
  label: string;
  pattern: RegExp;
  categories: TprSpecialtyCategory[];
};

const { GENERAL, INTERVENTIONAL, STRUCTURAL, ELECTROPHYSIOLOGY } = TprSpecialtyCategory;

/** Ops-facing labels for the four categories — TPR ops says "EP", not "electrophysiology". */
const TPR_SPECIALTY_CATEGORY_LABELS: Readonly<Record<TprSpecialtyCategory, string>> = {
  [GENERAL]: "General",
  [INTERVENTIONAL]: "Interventional",
  [STRUCTURAL]: "Structural",
  [ELECTROPHYSIOLOGY]: "EP",
};

/** Comma-joined ops labels for the reviewer UI, e.g. "Interventional, EP". Empty in, empty out. */
export function describeTprSpecialtyCategories(categories: readonly TprSpecialtyCategory[]): string {
  return categories.map((category) => TPR_SPECIALTY_CATEGORY_LABELS[category]).join(", ");
}

// Keyword -> specialty category map transcribed from TPR ops' diagnosis/condition reference table
// (same table as the checklist's now-removed specialty-roster.ts). Style follows
// referral-urgency.ts's URGENT_DIAGNOSIS_PATTERNS (word-boundary regexes, case insensitive).
// "Atrial Fibrillation" intentionally maps to BOTH Interventional and Electrophysiology per the
// source table — either receiving-provider specialty is acceptable for an afib diagnosis.
const DIAGNOSIS_SPECIALTY_PATTERNS: DiagnosisSpecialtyPattern[] = [
  // GENERAL
  { label: "Cardiomegaly", pattern: /\bcardiomegall?y\b/gi, categories: [GENERAL] },
  { label: "Chest Pain", pattern: /\bchest pain\b/gi, categories: [GENERAL] },
  { label: "Angina", pattern: /\bangina\b/gi, categories: [GENERAL] },
  { label: "Coronary Artery Disease", pattern: /\b(?:coronary artery disease|cad)\b/gi, categories: [GENERAL] },
  { label: "Hyperlipidemia", pattern: /\bhyperlipidemia\b/gi, categories: [GENERAL] },
  {
    label: "Hypertension",
    pattern: /\b(?:hypertension|hypertensive(?: heart disease)?|high blood pressure)\b/gi,
    categories: [GENERAL],
  },
  { label: "Low Blood Pressure", pattern: /\blow blood pressure\b/gi, categories: [GENERAL] },
  { label: "Syncope", pattern: /\b(?:syncope|fainting)\b/gi, categories: [GENERAL] },
  { label: "Shortness of Breath", pattern: /\b(?:shortness of breath|\bsob\b)\b/gi, categories: [GENERAL] },
  { label: "Palpitations", pattern: /\bpalpitations?\b/gi, categories: [GENERAL] },
  { label: "Murmur", pattern: /\bmurmur\b/gi, categories: [GENERAL] },
  { label: "Family History", pattern: /\bfamily history\b/gi, categories: [GENERAL] },
  { label: "Bradycardia", pattern: /\bbradycardia\b/gi, categories: [GENERAL] },
  { label: "Tachycardia", pattern: /\btachycardia\b/gi, categories: [GENERAL] },
  { label: "Nausea", pattern: /\bnausea\b/gi, categories: [GENERAL] },
  { label: "Cardiac Clearance", pattern: /\bcardiac clearance\b/gi, categories: [GENERAL] },
  { label: "Abnormal EKG", pattern: /\babnormal ekg\b/gi, categories: [GENERAL] },

  // INTERVENTIONAL
  { label: "Heart Failure", pattern: /\bheart failure\b/gi, categories: [INTERVENTIONAL] },
  { label: "Cardiomyopathy", pattern: /\bcardiomyopathy\b/gi, categories: [INTERVENTIONAL] },
  {
    label: "Atrial Fibrillation",
    pattern: /\b(?:a[\s.-]*fib|atrial fibrillation)\b/gi,
    categories: [INTERVENTIONAL, ELECTROPHYSIOLOGY],
  },
  { label: "Blood Clots", pattern: /\bblood clots?\b/gi, categories: [INTERVENTIONAL] },
  { label: "Stroke", pattern: /\bstroke\b/gi, categories: [INTERVENTIONAL] },
  { label: "Ischemic Heart Disease", pattern: /\bischemic heart disease\b/gi, categories: [INTERVENTIONAL] },
  { label: "Heart Attack", pattern: /\bheart attack\b/gi, categories: [INTERVENTIONAL] },
  { label: "Aneurysm", pattern: /\baneurysm\b/gi, categories: [INTERVENTIONAL] },
  { label: "Peripheral Artery Disease", pattern: /\bperipheral artery disease\b/gi, categories: [INTERVENTIONAL] },

  // STRUCTURAL
  { label: "Aortic Stenosis", pattern: /\baortic stenosis\b/gi, categories: [STRUCTURAL] },
  { label: "Aortic Regurgitation", pattern: /\baortic regurg(?:itation)?\b/gi, categories: [STRUCTURAL] },
  { label: "Mitral Stenosis", pattern: /\bmitral stenosis\b/gi, categories: [STRUCTURAL] },
  { label: "Mitral Regurgitation", pattern: /\bmitral regurg(?:itation)?\b/gi, categories: [STRUCTURAL] },
  { label: "Valve Insufficiency", pattern: /\bvalve insufficiency\b/gi, categories: [STRUCTURAL] },
  { label: "Valve Prolapse", pattern: /\bvalve prolapse\b/gi, categories: [STRUCTURAL] },
  { label: "Congenital Heart Disease", pattern: /\bcongenital heart disease\b/gi, categories: [STRUCTURAL] },
  { label: "Left Atrial Appendage", pattern: /\bleft atrial appendage\b/gi, categories: [STRUCTURAL] },
  { label: "Renal Denervation", pattern: /\brenal denervation\b/gi, categories: [STRUCTURAL] },

  // ELECTROPHYSIOLOGY
  { label: "Heart Rhythm Disorder", pattern: /\bheart rhythm disorder\b/gi, categories: [ELECTROPHYSIOLOGY] },
  { label: "Atrial Flutter", pattern: /\batrial flutter\b/gi, categories: [ELECTROPHYSIOLOGY] },
  {
    label: "Supraventricular Tachycardia",
    pattern: /\b(?:supraventricular tachycardia|svt)\b/gi,
    categories: [ELECTROPHYSIOLOGY],
  },
  { label: "Device Implantation", pattern: /\bdevice implantation\b/gi, categories: [ELECTROPHYSIOLOGY] },
  { label: "Device Monitoring", pattern: /\bdevice monitoring\b/gi, categories: [ELECTROPHYSIOLOGY] },
  { label: "Pacemaker", pattern: /\bpacemaker\b/gi, categories: [ELECTROPHYSIOLOGY] },
  { label: "ICD", pattern: /\bicd\b/gi, categories: [ELECTROPHYSIOLOGY] },
];

/**
 * All specialty categories whose keyword pattern matched somewhere in the referral's diagnosis
 * text. Takes the full `Referral` (rather than a `Pick`) because `Referral` is a discriminated
 * union over `ReferralDocumentType`, and not every variant's Zod schema includes
 * reasonForReferral/diagnosisList — see `getTprReferralUrgencyNote` in referral-urgency.ts for the
 * same pattern.
 */
export function matchTprDiagnosisSpecialties(referral: Referral): TprSpecialtyCategory[] {
  const texts = [
    referral.reasonForReferral,
    ...((referral.diagnosisList ?? []) as DiagnosisItem[]).map((diagnosis) => diagnosis.diagnosisText),
  ];
  const matched = new Set<TprSpecialtyCategory>();

  for (const { pattern, categories } of DIAGNOSIS_SPECIALTY_PATTERNS) {
    if (texts.some((text) => textMatchesUnnegated(text, pattern))) {
      categories.forEach((category) => matched.add(category));
    }
  }

  return Array.from(matched);
}

/**
 * Physician NPI -> department IDs they practice at, transcribed from ops' provider-roster
 * reference sheet (2026-09-01). This roster deliberately supersedes the per-location NPI enums in
 * configuration.ts (`TprBiltmoreCamelbackProviderNpi` etc.) for "where does this physician
 * practice" purposes — those enums group a physician under a single location for other reasons
 * (e.g. Camelback in-scope gating) and materially undercount most physicians' actual practicing
 * locations. Do not derive location coverage from those enums; this is the source of truth for
 * that question.
 *
 * Known discrepancy: ops' sheet lists Saifuldeen Al Qaisi as West-only, while
 * `TprBiltmorePeoriaProviderNpi` groups him under Peoria. Following the sheet here; flag to ops if
 * that enum grouping should also change.
 *
 * Supervised APPs (Diana Thayer, Danielle Sturm, Kristen Jensen, Kevin Murphy, Elizabeth Steffens)
 * resolve through their supervising physician (`getTprSupervisingPhysicianNpi`). Independent
 * clinicians remain direct roster entries.
 */
export const TPR_PHYSICIAN_LOCATIONS: Readonly<Record<string, readonly string[]>> = {
  [TprBiltmoreCamelbackProviderNpi.AKIL_LOLI]: [TPR_BILTMORE_DEPARTMENT_ID, TPR_BILTMORE_CASA_GRANDE_DEPARTMENT_ID],
  [TprBiltmoreCamelbackProviderNpi.AKRAM_IBRAHIM]: [TPR_BILTMORE_DEPARTMENT_ID],
  [TprBiltmoreCamelbackProviderNpi.CHRIS_LICHTENWALTER]: [TPR_BILTMORE_DEPARTMENT_ID],
  [TprBiltmoreScottsdaleProviderNpi.ASHISH_GUPTA]: [
    TPR_BILTMORE_SCOTTSDALE_DEPARTMENT_ID,
    TPR_BILTMORE_ARROWHEAD_DEPARTMENT_ID,
    TPR_BILTMORE_SURPRISE_DEPARTMENT_ID,
  ],
  [TprBiltmoreAhiProviderNpi.AYMAN_JAMAL]: [TPR_BILTMORE_AHI_DEPARTMENT_ID],
  [TprBiltmoreArrowheadProviderNpi.DAVID_PRUTZMAN]: [
    TPR_BILTMORE_ARROWHEAD_DEPARTMENT_ID,
    TPR_BILTMORE_SURPRISE_DEPARTMENT_ID,
    TPR_BILTMORE_PEORIA_DEPARTMENT_ID,
  ],
  [TprBiltmoreWestProviderNpi.JAMES_KIM]: [TPR_BILTMORE_VERRADO_DEPARTMENT_ID, TPR_BILTMORE_WEST_DEPARTMENT_ID],
  [TprBiltmoreCasaGrandeProviderNpi.JASON_COOL]: [TPR_BILTMORE_CASA_GRANDE_DEPARTMENT_ID],
  [TprBiltmoreArrowheadProviderNpi.KRISTINA_SKINNER]: [
    TPR_BILTMORE_ARROWHEAD_DEPARTMENT_ID,
    TPR_BILTMORE_SURPRISE_DEPARTMENT_ID,
    TPR_BILTMORE_PEORIA_DEPARTMENT_ID,
  ],
  [TprBiltmoreCamelbackProviderNpi.LAWRENCE_KLINE]: [TPR_BILTMORE_DEPARTMENT_ID, TPR_BILTMORE_AHI_DEPARTMENT_ID],
  [TprBiltmoreCamelbackProviderNpi.LORRAINE_HOMES]: [TPR_BILTMORE_DEPARTMENT_ID],
  [TprBiltmoreArrowheadProviderNpi.MAEDEH_KHOLGHI]: [
    TPR_BILTMORE_ARROWHEAD_DEPARTMENT_ID,
    TPR_BILTMORE_SURPRISE_DEPARTMENT_ID,
    TPR_BILTMORE_PEORIA_DEPARTMENT_ID,
  ],
  [TprBiltmoreCamelbackProviderNpi.MARIUM_MUZAFFAR]: [TPR_BILTMORE_DEPARTMENT_ID],
  [TprBiltmoreCamelbackProviderNpi.MARWAN_BAHU]: [TPR_BILTMORE_DEPARTMENT_ID],
  [TprBiltmoreAhiProviderNpi.MICHELLE_MIX]: [TPR_BILTMORE_AHI_DEPARTMENT_ID],
  [TprBiltmoreCamelbackProviderNpi.NEAL_KLEIN]: [TPR_BILTMORE_DEPARTMENT_ID],
  [TprBiltmoreCamelbackProviderNpi.NICKALAUS_GRAMZE]: [TPR_BILTMORE_DEPARTMENT_ID],
  [TprBiltmoreCamelbackProviderNpi.PETER_MAKI]: [TPR_BILTMORE_DEPARTMENT_ID],
  [TprBiltmoreCamelbackProviderNpi.JODY_ECKHARDT]: [TPR_BILTMORE_DEPARTMENT_ID],
  [TprBiltmoreArrowheadProviderNpi.RAHOOL_KARNIK]: [
    TPR_BILTMORE_ARROWHEAD_DEPARTMENT_ID,
    TPR_BILTMORE_PEORIA_DEPARTMENT_ID,
  ],
  [TprBiltmoreCamelbackProviderNpi.RENZO_CATALDO]: [
    TPR_BILTMORE_GOODYEAR_DEPARTMENT_ID,
    TPR_BILTMORE_VERRADO_DEPARTMENT_ID,
    TPR_BILTMORE_DEPARTMENT_ID,
  ],
  [TprBiltmoreWestProviderNpi.RICHARD_PHAM]: [TPR_BILTMORE_VERRADO_DEPARTMENT_ID, TPR_BILTMORE_WEST_DEPARTMENT_ID],
  [TprBiltmorePeoriaProviderNpi.SAIFULDEEN_AL_QAISI]: [TPR_BILTMORE_WEST_DEPARTMENT_ID],
  [TprBiltmoreGoodyearProviderNpi.SHARON_SADEGHINIA]: [
    TPR_BILTMORE_GOODYEAR_DEPARTMENT_ID,
    TPR_BILTMORE_SURPRISE_DEPARTMENT_ID,
    TPR_BILTMORE_WEST_DEPARTMENT_ID,
  ],
  [TprBiltmoreCamelbackProviderNpi.TIMOTHY_BYRNE]: [TPR_BILTMORE_DEPARTMENT_ID],
  [TprBiltmoreScottsdaleProviderNpi.WILLIAM_JAFFE]: [
    TPR_BILTMORE_SCOTTSDALE_DEPARTMENT_ID,
    TPR_BILTMORE_CENTRAL_DEPARTMENT_ID,
  ],
};

/**
 * Checked providers on the Biltmore Locations "Providers" tab (gid 195970917).
 * This is narrower than the general scheduling roster: unchecked or absent providers must not
 * establish receiving-provider continuity from completed past appointments.
 */
export const TPR_APPOINTMENT_HISTORY_PROVIDER_NPIS: ReadonlySet<string> = new Set([
  TprBiltmoreCamelbackProviderNpi.AKIL_LOLI,
  TprBiltmoreCamelbackProviderNpi.AKRAM_IBRAHIM,
  TprBiltmoreScottsdaleProviderNpi.ASHISH_GUPTA,
  TprBiltmoreAhiProviderNpi.AYMAN_JAMAL,
  TprBiltmoreArrowheadProviderNpi.DAVID_PRUTZMAN,
  TprBiltmoreWestProviderNpi.JAMES_KIM,
  TprBiltmoreCasaGrandeProviderNpi.JASON_COOL,
  TprBiltmoreArrowheadProviderNpi.KRISTINA_SKINNER,
  TprBiltmoreCamelbackProviderNpi.LAWRENCE_KLINE,
  TprBiltmoreArrowheadProviderNpi.MAEDEH_KHOLGHI,
  TprBiltmoreCamelbackProviderNpi.MARIUM_MUZAFFAR,
  TprBiltmoreCamelbackProviderNpi.MARWAN_BAHU,
  TprBiltmoreAhiProviderNpi.MICHELLE_MIX,
  TprBiltmoreCamelbackProviderNpi.NEAL_KLEIN,
  TprBiltmoreCamelbackProviderNpi.NICKALAUS_GRAMZE,
  TprBiltmoreCamelbackProviderNpi.PETER_MAKI,
  TprBiltmoreArrowheadProviderNpi.RAHOOL_KARNIK,
  TprBiltmoreCamelbackProviderNpi.RENZO_CATALDO,
  TprBiltmoreWestProviderNpi.RICHARD_PHAM,
  TprBiltmorePeoriaProviderNpi.SAIFULDEEN_AL_QAISI,
  TprBiltmoreGoodyearProviderNpi.SHARON_SADEGHINIA,
  TprBiltmoreCamelbackProviderNpi.TIMOTHY_BYRNE,
  TprBiltmoreScottsdaleProviderNpi.WILLIAM_JAFFE,
]);

/** Same ops sheet as TPR_PHYSICIAN_LOCATIONS — specialty categories per physician NPI. */
export const TPR_PHYSICIAN_SPECIALTIES: Readonly<Record<string, readonly TprSpecialtyCategory[]>> = {
  [TprBiltmoreCamelbackProviderNpi.AKIL_LOLI]: [GENERAL, INTERVENTIONAL, STRUCTURAL],
  [TprBiltmoreCamelbackProviderNpi.AKRAM_IBRAHIM]: [GENERAL, INTERVENTIONAL, STRUCTURAL],
  [TprBiltmoreCamelbackProviderNpi.CHRIS_LICHTENWALTER]: [GENERAL, INTERVENTIONAL],
  [TprBiltmoreScottsdaleProviderNpi.ASHISH_GUPTA]: [GENERAL],
  [TprBiltmoreAhiProviderNpi.AYMAN_JAMAL]: [GENERAL, INTERVENTIONAL, STRUCTURAL],
  [TprBiltmoreArrowheadProviderNpi.DAVID_PRUTZMAN]: [GENERAL, INTERVENTIONAL, STRUCTURAL],
  [TprBiltmoreWestProviderNpi.JAMES_KIM]: [GENERAL, INTERVENTIONAL],
  [TprBiltmoreCasaGrandeProviderNpi.JASON_COOL]: [GENERAL],
  [TprBiltmoreArrowheadProviderNpi.KRISTINA_SKINNER]: [GENERAL, INTERVENTIONAL],
  [TprBiltmoreCamelbackProviderNpi.LAWRENCE_KLINE]: [GENERAL],
  [TprBiltmoreCamelbackProviderNpi.LORRAINE_HOMES]: [GENERAL],
  [TprBiltmoreArrowheadProviderNpi.MAEDEH_KHOLGHI]: [GENERAL],
  [TprBiltmoreCamelbackProviderNpi.MARIUM_MUZAFFAR]: [GENERAL, INTERVENTIONAL],
  [TprBiltmoreCamelbackProviderNpi.MARWAN_BAHU]: [ELECTROPHYSIOLOGY],
  [TprBiltmoreAhiProviderNpi.MICHELLE_MIX]: [GENERAL],
  [TprBiltmoreCamelbackProviderNpi.NEAL_KLEIN]: [GENERAL, INTERVENTIONAL],
  [TprBiltmoreCamelbackProviderNpi.NICKALAUS_GRAMZE]: [GENERAL],
  [TprBiltmoreCamelbackProviderNpi.PETER_MAKI]: [GENERAL, INTERVENTIONAL],
  [TprBiltmoreCamelbackProviderNpi.JODY_ECKHARDT]: [GENERAL],
  [TprBiltmoreArrowheadProviderNpi.RAHOOL_KARNIK]: [GENERAL],
  [TprBiltmoreCamelbackProviderNpi.RENZO_CATALDO]: [ELECTROPHYSIOLOGY],
  [TprBiltmoreWestProviderNpi.RICHARD_PHAM]: [GENERAL, INTERVENTIONAL],
  [TprBiltmorePeoriaProviderNpi.SAIFULDEEN_AL_QAISI]: [GENERAL, INTERVENTIONAL],
  [TprBiltmoreGoodyearProviderNpi.SHARON_SADEGHINIA]: [GENERAL],
  [TprBiltmoreCamelbackProviderNpi.TIMOTHY_BYRNE]: [GENERAL, INTERVENTIONAL, STRUCTURAL],
  [TprBiltmoreScottsdaleProviderNpi.WILLIAM_JAFFE]: [GENERAL],
};

/** Same ops sheet — display name (with credentials) per physician NPI, for the receiving-provider resolver's display. */
export const TPR_PHYSICIAN_DISPLAY_NAME: Readonly<Record<string, string>> = {
  [TprBiltmoreCamelbackProviderNpi.AKIL_LOLI]: "Akil Loli, MD",
  [TprBiltmoreCamelbackProviderNpi.AKRAM_IBRAHIM]: "Akram Ibrahim, MD",
  [TprBiltmoreCamelbackProviderNpi.CHRIS_LICHTENWALTER]: "Chris Lichtenwalter, MD",
  [TprBiltmoreScottsdaleProviderNpi.ASHISH_GUPTA]: "Ashish Gupta, MD",
  [TprBiltmoreAhiProviderNpi.AYMAN_JAMAL]: "Ayman Jamal, MD",
  [TprBiltmoreArrowheadProviderNpi.DAVID_PRUTZMAN]: "David Prutzman, DO",
  [TprBiltmoreWestProviderNpi.JAMES_KIM]: "James Kim, MD",
  [TprBiltmoreCasaGrandeProviderNpi.JASON_COOL]: "Jason Cool, MD",
  [TprBiltmoreArrowheadProviderNpi.KRISTINA_SKINNER]: "Kristina Skinner, DO",
  [TprBiltmoreCamelbackProviderNpi.LAWRENCE_KLINE]: "Lawrence Kline, DO",
  [TprBiltmoreCamelbackProviderNpi.LORRAINE_HOMES]: "Lorraine Homes, FNP-C",
  [TprBiltmoreArrowheadProviderNpi.MAEDEH_KHOLGHI]: "Maedeh Kholghi, DO",
  [TprBiltmoreCamelbackProviderNpi.MARIUM_MUZAFFAR]: "Marium Muzaffar, MD",
  [TprBiltmoreCamelbackProviderNpi.MARWAN_BAHU]: "Marwan Bahu, MD",
  [TprBiltmoreAhiProviderNpi.MICHELLE_MIX]: "Michelle Mix, MD",
  [TprBiltmoreCamelbackProviderNpi.NEAL_KLEIN]: "Neal Klein, MD",
  [TprBiltmoreCamelbackProviderNpi.NICKALAUS_GRAMZE]: "Nickalaus Gramze, MD",
  [TprBiltmoreCamelbackProviderNpi.PETER_MAKI]: "Peter Maki, MD",
  [TprBiltmoreCamelbackProviderNpi.JODY_ECKHARDT]: "Jody Eckhardt, DNP",
  [TprBiltmoreArrowheadProviderNpi.RAHOOL_KARNIK]: "Rahool Karnik, MD",
  [TprBiltmoreCamelbackProviderNpi.RENZO_CATALDO]: "Renzo Cataldo, MD",
  [TprBiltmoreWestProviderNpi.RICHARD_PHAM]: "Richard Pham, DO",
  [TprBiltmorePeoriaProviderNpi.SAIFULDEEN_AL_QAISI]: "Saifuldeen Al Qaisi, MD",
  [TprBiltmoreGoodyearProviderNpi.SHARON_SADEGHINIA]: "Sharon Sadeghinia, MD",
  [TprBiltmoreCamelbackProviderNpi.TIMOTHY_BYRNE]: "Timothy Byrne, DO",
  [TprBiltmoreScottsdaleProviderNpi.WILLIAM_JAFFE]: "William Jaffe, DO",
};

/** Display name (with credentials) for a physician NPI, falling back to the supervising physician for an APP. */
export function getTprPhysicianDisplayName(providerNpi: string | null | undefined): string | null {
  if (!providerNpi) return null;

  const direct = TPR_PHYSICIAN_DISPLAY_NAME[providerNpi];

  if (direct) return direct;

  const supervisingPhysicianNpi = getTprSupervisingPhysicianNpi(providerNpi);

  return (supervisingPhysicianNpi && TPR_PHYSICIAN_DISPLAY_NAME[supervisingPhysicianNpi]) || null;
}

export function getTprAppointmentProviderDisplayName(
  appointmentProviderName: string,
  appointmentProviderNpi: string | null | undefined
): string {
  if (!appointmentProviderNpi) return appointmentProviderName;

  const supervisingPhysicianNpi = getTprSupervisingPhysicianNpi(appointmentProviderNpi);

  if (supervisingPhysicianNpi) {
    const supervisingPhysicianName = getTprPhysicianDisplayName(supervisingPhysicianNpi);

    return supervisingPhysicianName
      ? `${appointmentProviderName} → supervising physician ${supervisingPhysicianName}`
      : appointmentProviderName;
  }

  return getTprPhysicianDisplayName(appointmentProviderNpi) ?? appointmentProviderName;
}

/**
 * Department IDs a physician practices at. Resolves an APP NPI to its supervising physician's
 * roster entry first (APPs aren't in the ops sheet — see the module doc comment). Returns an empty
 * array when the NPI isn't in the roster at all (unknown physician, or an APP with no supervising
 * physician on file).
 */
export function getTprPhysicianLocations(providerNpi: string | null | undefined): readonly string[] {
  if (!providerNpi) return [];

  const direct = TPR_PHYSICIAN_LOCATIONS[providerNpi];

  if (direct) return direct;

  const supervisingPhysicianNpi = getTprSupervisingPhysicianNpi(providerNpi);

  return (supervisingPhysicianNpi && TPR_PHYSICIAN_LOCATIONS[supervisingPhysicianNpi]) || [];
}

/**
 * Every department at least one rostered physician practices at, nearest-first callers can rank
 * against. Deliberately narrower than `getTprDepartmentIds()`: the sites missing from the ops
 * roster sheet (Terrace, Wickenburg, Kierland) are valid receiving locations but have nobody to
 * assign, so proposing one on proximity alone would only manufacture manual review.
 */
export function getTprStaffedDepartmentIds(): string[] {
  return [...new Set(Object.values(TPR_PHYSICIAN_LOCATIONS).flat())].sort((a, b) =>
    a.localeCompare(b, "en", { numeric: true })
  );
}

function getTprPhysicianSpecialties(providerNpi: string): readonly TprSpecialtyCategory[] {
  const direct = TPR_PHYSICIAN_SPECIALTIES[providerNpi];

  if (direct) return direct;

  const supervisingPhysicianNpi = getTprSupervisingPhysicianNpi(providerNpi);

  return (supervisingPhysicianNpi && TPR_PHYSICIAN_SPECIALTIES[supervisingPhysicianNpi]) || [];
}

export function doesTprPhysicianTreatSpecialties(
  providerNpi: string,
  categories: readonly TprSpecialtyCategory[]
): boolean {
  return getTprPhysicianSpecialties(providerNpi).some((category) => categories.includes(category));
}

/** True when the given physician (or their supervising physician, for an APP) practices at the department. */
export function doesTprPhysicianPracticeAtDepartment(
  providerNpi: string | null | undefined,
  departmentId: string
): boolean {
  return getTprPhysicianLocations(providerNpi).includes(departmentId);
}

/**
 * Physician NPIs who both treat at least one of the given specialty categories AND practice at the
 * given department — candidates for the receiving-provider resolver's "no receiving provider named,
 * pick one at this location who treats the diagnosis" branch. Sorted for a stable, deterministic
 * first choice rather than object-key iteration order.
 */
export function findTprPhysiciansForSpecialtiesAtDepartment(
  categories: readonly TprSpecialtyCategory[],
  departmentId: string
): string[] {
  return Object.keys(TPR_PHYSICIAN_LOCATIONS)
    .filter(
      (npi) =>
        doesTprPhysicianPracticeAtDepartment(npi, departmentId) && doesTprPhysicianTreatSpecialties(npi, categories)
    )
    .sort();
}

export type TprPhysicianOption = {
  providerNpi: string;
  displayName: string;
};

/**
 * Every rostered physician practising at a department, for the ops provider picker on the sync
 * steps. Scoped to the department on purpose: picking a provider who doesn't practise where the
 * referral is going is what produces the wrong-location care-team member, so the picker should not
 * offer the invalid pairs at all.
 *
 * Sorted by display name so the list is stable and scannable rather than in object-key order.
 */
export function listTprPhysiciansAtDepartment(departmentId: string): TprPhysicianOption[] {
  return Object.keys(TPR_PHYSICIAN_LOCATIONS)
    .filter((npi) => doesTprPhysicianPracticeAtDepartment(npi, departmentId))
    .map((npi) => ({ providerNpi: npi, displayName: getTprPhysicianDisplayName(npi) ?? npi }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}
